import React, { useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebaseConfig';
import { exportCsv, exportXlsx, exportPdf } from '../utils/exporters';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  TextField,
  Button,
  Divider,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

/**
 * Look up one customer by email and show their card transactions and parking
 * sessions — for helping with a refund, so you can confirm who you are checking
 * and see the exact transaction (with its gateway transaction id).
 *
 * Read-only. Admin only (it lives in the admin dashboard and reads across all
 * payments/sessions, which Firestore rules permit only for admins).
 */

function parseDate(v: any): Date | null {
  if (v === null || v === undefined) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (typeof v?.seconds === 'number') return new Date(v.seconds * 1000);
  if (typeof v === 'string' || typeof v === 'number') {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}
function money(n: number): string {
  return `$${(Number(n) || 0).toFixed(2)}`;
}

interface Txn {
  when: Date | null;
  whenLabel: string;
  lot: string;
  amount: number;
  parkingFee: number;
  salesTax: number;
  processingFee: number;
  gateway: string;
  txnId: string;
  currency: string;
}
interface Sess {
  when: Date | null;
  whenLabel: string;
  lot: string;
  vehicle: string;
  minutes: number;
  amount: number;
  status: string;
}

export default function UserLookup({ lockedVendorId }: { lockedVendorId?: string } = {}) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [txns, setTxns] = useState<Txn[]>([]);
  const [sessions, setSessions] = useState<Sess[]>([]);
  const [foundEmail, setFoundEmail] = useState('');

  async function search() {
    const raw = email.trim();
    if (!raw) return;
    setLoading(true);
    try {
      // Partner mode: go through the cloud function, which scopes the lookup to
      // this partner's own payments and lots (a partner cannot read those
      // collections directly, and must only see activity at their locations).
      if (lockedVendorId) {
        const fn = httpsCallable(functions, 'getPartnerReport');
        const res: any = await fn({ report: 'userlookup', email: raw, vendorId: lockedVendorId });
        const d = res.data || {};
        const t: Txn[] = (d.txns || []).map((r: any) => {
          const when = new Date(r.ts);
          return {
            when,
            whenLabel: when.toLocaleString(),
            lot: r.lotName || '—',
            amount: Number(r.amount) || 0,
            parkingFee: Number(r.parkingFee) || 0,
            salesTax: Number(r.salesTax) || 0,
            processingFee: Number(r.processingFee) || 0,
            gateway: r.gateway || '—',
            txnId: r.transactionId || '—',
            currency: r.currency || 'USD',
          };
        });
        t.sort((a, b) => (b.when?.getTime() || 0) - (a.when?.getTime() || 0));
        const s: Sess[] = (d.sessions || []).map((r: any) => {
          const when = new Date(r.ts);
          return {
            when,
            whenLabel: when.toLocaleString(),
            lot: r.lotName || '—',
            vehicle: r.vehicle || '—',
            minutes: Number(r.minutes) || 0,
            amount: Number(r.amount) || 0,
            status: r.active ? 'Active' : 'Closed',
          };
        });
        s.sort((a, b) => (b.when?.getTime() || 0) - (a.when?.getTime() || 0));
        setTxns(t);
        setSessions(s);
        setFoundEmail(raw);
        setSearched(true);
        return;
      }

      // Emails are usually stored lowercase; query both to be safe and merge.
      const candidates = Array.from(new Set([raw, raw.toLowerCase()]));

      const payDocs: Record<string, any> = {};
      const sessDocs: Record<string, any> = {};
      for (const e of candidates) {
        const [ps, ss] = await Promise.all([
          getDocs(query(collection(db, 'payments'), where('customerInfo.email', '==', e))),
          getDocs(query(collection(db, 'parkingsessions'), where('customerInfo.email', '==', e))),
        ]);
        ps.docs.forEach((d) => (payDocs[d.id] = d.data()));
        ss.docs.forEach((d) => (sessDocs[d.id] = d.data()));
      }

      const t: Txn[] = Object.values(payDocs).map((data: any) => {
        const when = parseDate(data.receiptTS);
        return {
          when,
          whenLabel: when ? when.toLocaleString() : '—',
          lot: data.lotName || '—',
          amount: Number(data.amount) || 0,
          parkingFee: Number(data.parkingFee) || 0,
          salesTax: Number(data.salesTax) || 0,
          processingFee: Number(data.processingFee) || 0,
          gateway: data.gateway || '—',
          txnId: data.transactionId || '—',
          currency: data.currency || 'USD',
        };
      });
      t.sort((a, b) => (b.when?.getTime() || 0) - (a.when?.getTime() || 0));

      const s: Sess[] = Object.values(sessDocs).map((data: any) => {
        const when = parseDate(data.startTime);
        const v = data.vehicleInfo || {};
        return {
          when,
          whenLabel: when ? when.toLocaleString() : '—',
          lot: data.lotName || '—',
          vehicle: [v.color, v.make, v.licensePlate].filter(Boolean).join(' ') || '—',
          minutes: Number(data.duration) || 0,
          amount: Number(data.amount) || 0,
          status: data.sessionActive ? 'Active' : 'Closed',
        };
      });
      s.sort((a, b) => (b.when?.getTime() || 0) - (a.when?.getTime() || 0));

      setTxns(t);
      setSessions(s);
      setFoundEmail(raw);
      setSearched(true);
    } catch (e) {
      console.error('UserLookup: search failed', e);
      alert('Could not run the lookup. See the console for details.');
    } finally {
      setLoading(false);
    }
  }

  const totalCharged = txns.reduce((sum, r) => sum + r.amount, 0);

  function exportData(): { title: string; base: string; headers: string[]; rows: (string | number)[][] } {
    return {
      title: `Transactions — ${foundEmail}`,
      base: `user_${foundEmail}`.replace(/[^a-zA-Z0-9._@-]/g, '_'),
      headers: ['When', 'Location', 'Parking', 'Tax', 'Fee', 'Total', 'Gateway', 'TransactionId'],
      rows: txns.map((r) => [
        r.whenLabel, r.lot, r.parkingFee.toFixed(2), r.salesTax.toFixed(2),
        r.processingFee.toFixed(2), r.amount.toFixed(2), r.gateway, r.txnId,
      ]),
    };
  }
  const doCsv = () => {
    const e = exportData();
    exportCsv(`${e.base}.csv`, e.headers, e.rows);
  };
  const doXlsx = () => {
    const e = exportData();
    exportXlsx(`${e.base}.xlsx`, e.headers, e.rows);
  };
  const doPdf = () => {
    const e = exportData();
    exportPdf(e.title, `${e.base}.pdf`, e.headers, e.rows);
  };
  const doPrint = () => window.print();

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        User Lookup
      </Typography>
      <Typography color="textSecondary" sx={{ mb: 2 }}>
        Find a customer by email to review their transactions and sessions — for refund help.
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }} className="no-print">
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Customer email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') search();
            }}
            size="small"
            sx={{ minWidth: 320 }}
            placeholder="name@example.com"
          />
          <Button variant="contained" onClick={search} disabled={loading || !email.trim()}>
            {loading ? 'Searching…' : 'Search'}
          </Button>
        </Box>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && searched && (
        <>
          <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
            <Card sx={{ flex: '1 1 240px', minWidth: 220 }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Customer
                </Typography>
                <Typography variant="h6" sx={{ wordBreak: 'break-all' }}>
                  {foundEmail}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 200px', minWidth: 180 }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Charged
                </Typography>
                <Typography variant="h4">{money(totalCharged)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 200px', minWidth: 180 }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Transactions
                </Typography>
                <Typography variant="h4">{txns.length}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 200px', minWidth: 180 }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Sessions
                </Typography>
                <Typography variant="h4">{sessions.length}</Typography>
              </CardContent>
            </Card>
          </Box>

          <Box className="no-print" sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            <Button variant="outlined" size="small" onClick={doPrint}>
              Print
            </Button>
            <Button variant="outlined" size="small" onClick={doPdf} disabled={txns.length === 0}>
              PDF
            </Button>
            <Button variant="outlined" size="small" onClick={doXlsx} disabled={txns.length === 0}>
              Excel
            </Button>
            <Button variant="outlined" size="small" onClick={doCsv} disabled={txns.length === 0}>
              CSV
            </Button>
          </Box>

          {txns.length === 0 && sessions.length === 0 && (
            <Typography color="textSecondary">
              No transactions or sessions found for <strong>{foundEmail}</strong>. Check the spelling —
              email must match exactly.
            </Typography>
          )}

          {txns.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                Transactions ({txns.length})
              </Typography>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell align="right">Parking</TableCell>
                      <TableCell align="right">Tax</TableCell>
                      <TableCell align="right">Fee</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Gateway</TableCell>
                      <TableCell>Transaction ID</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {txns.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>{r.whenLabel}</TableCell>
                        <TableCell>{r.lot}</TableCell>
                        <TableCell align="right">{money(r.parkingFee)}</TableCell>
                        <TableCell align="right">{money(r.salesTax)}</TableCell>
                        <TableCell align="right">{money(r.processingFee)}</TableCell>
                        <TableCell align="right">{money(r.amount)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={r.gateway} />
                        </TableCell>
                        <TableCell>{r.txnId}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {sessions.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Sessions ({sessions.length})
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Vehicle</TableCell>
                      <TableCell align="right">Min</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sessions.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>{r.whenLabel}</TableCell>
                        <TableCell>{r.lot}</TableCell>
                        <TableCell>{r.vehicle}</TableCell>
                        <TableCell align="right">{r.minutes}</TableCell>
                        <TableCell align="right">{money(r.amount)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={r.status}
                            color={r.status === 'Active' ? 'success' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}
    </Box>
  );
}
